"use client";

import React from "react";

import {
  InheritedStylesProvider,
  useMergeStyles
} from "../../../hooks/useInheritedStyles";
import { Box, BoxDiv } from "../../Box";
import {
  FontSize,
  FontWeight,
  LetterSpacing,
  LineHeight,
  ThemeFontSizes,
  ThemeFontWeights,
  ThemeLetterSpaces
} from "../../Theme";
import { TypographyVariants } from "./TypographyVariants";

type TextSize = keyof typeof FontSize;
type TextSizeArray = [TextSize, TextSize, TextSize];
type TextSizeProp = TextSize | TextSizeArray;

type TextProps = {
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "label" | "div";
  variant?: keyof typeof TypographyVariants;
  weight?: "light" | "regular" | "medium" | "bold";
  size?: TextSizeProp;
  lineHeight?: keyof typeof LineHeight;
  letterSpacing?: keyof typeof LetterSpacing;
} & BoxDiv;

export const Text: React.FC<TextProps> = (props: TextProps) => {
  const {
    className,
    children,
    as,
    weight,
    size,
    lineHeight,
    letterSpacing,
    variant,
    sx = {}
  } = props;

  const computedAs = React.useMemo(() => {
    switch (variant) {
      case "h1":
      case "h1-light":
        return "h1";
      case "h2":
      case "h2-light":
        return "h2";
      case "h3":
      case "h3-light":
        return "h3";
      case "h4":
      case "h4-light":
        return "h4";
      case "h5":
      case "h5-light":
        return "h5";
      case "h6":
      case "h6-light":
        return "h6";
      default:
        return "span";
    }
  }, [variant]);

  const { newInheritedStylesContext, mergedSx } = useMergeStyles(sx);

  /* 
    Inheritance (high -> low):
    - component style: custom styles applied directly through props or sx 
    - variant style: pre-set styles applied through the variant
    - inherited style: styles inherited from parent node
    - default style: base styles set in context or by browser
  */

  const updatedStyleContext = React.useMemo<
    typeof newInheritedStylesContext
  >(() => {
    const computedFontSize = size
      ? size
      : variant
        ? TypographyVariants[variant].fontSize
        : newInheritedStylesContext.fontSize;

    const computedFontWeight = weight
      ? (`font-weight-${weight}` as keyof typeof FontWeight)
      : variant
        ? TypographyVariants[variant].fontWeight
        : newInheritedStylesContext.fontWeight;

    const computedLineHeight = lineHeight
      ? lineHeight
      : variant
        ? TypographyVariants[variant].lineHeight
        : newInheritedStylesContext.lineHeight;

    const computedLetterSpacing = letterSpacing
      ? letterSpacing
      : variant
        ? TypographyVariants[variant].letterSpacing
        : newInheritedStylesContext.letterSpacing;

    return {
      ...newInheritedStylesContext,
      fontSize: (computedFontSize as ThemeFontSizes) || "font-size-4",
      fontWeight:
        (computedFontWeight as ThemeFontWeights) || "font-weight-regular",
      lineHeight: (computedLineHeight as ThemeFontSizes) || "leading-2",
      letterSpacing: computedLetterSpacing as ThemeLetterSpaces
    };
  }, [
    letterSpacing,
    lineHeight,
    newInheritedStylesContext,
    size,
    variant,
    weight
  ]);

  return (
    <InheritedStylesProvider styleContext={updatedStyleContext}>
      <Box
        as={as || computedAs}
        className={className}
        sx={{
          ...mergedSx,
          ...updatedStyleContext,
          textTransform: variant === "overline" ? "uppercase" : "unset"
        }}
      >
        {children}
      </Box>
    </InheritedStylesProvider>
  );
};
